import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import MobileListToggle, { useMobileListVisibleCount } from "./MobileListToggle.tsx";
import { PlayerNameLabels } from "./PlayerNameLabels.tsx";
import { RatingWithChange } from "./RatingWithChange.tsx";
import { helpers } from "../util/helpers.ts";
import i18n from "../util/i18n.ts";
import { PositionText } from "../util/positionNames.tsx";

/**
 * Y-07/P2: card layout shared by Roster, FreeAgents, and NegotiationList.
 *
 * Generic over the player shape so each view can pass its own
 * `View<"roster">["players"][number]` / `View<"freeAgents">["players"][number]`
 * — fields that diverge between views (jersey number nesting,
 * dovr/dpot deltas, awards, contract presence, mood type, action
 * button row) come in via render slots and accessor props instead
 * of being branched inside the component.
 *
 * Y-06-c/P2-4: when `swipeRevealActions` is true, actions are kept behind an
 * explicit per-card button. renderMood always stays in the footer.
 * Default is false (actions visible in footer) so FreeAgents is unaffected.
 */

type BaseCardPlayer = {
  pid: number;
  age: number;
  injury: { type: string; gamesRemaining: number };
  watch: number | undefined;
  firstName: string;
  firstNameShort?: string;
  lastName: string;
  ratings: {
    pos: string;
    ovr: number;
    pot: number;
    skills: string[];
  };
};

type PlayerCardListProps<P extends BaseCardPlayer> = {
  players: P[];
  season: number;
  shouldShowRatings: (p: P) => boolean;
  getJerseyNumber: (p: P) => string | undefined;
  getOvrChange?: (p: P) => number | undefined;
  getPotChange?: (p: P) => number | undefined;
  getAwards?: (p: P) => any;
  getContract?: (p: P) => { amount: number; exp: number } | undefined;
  getPlayerHref?: (p: P) => string;
  onPlayerNameClick?: (event: MouseEvent<HTMLAnchorElement>, p: P) => void;
  /**
   * C2: optional leading media slot (face thumbnail / logo) rendered on the
   * left edge of the card. Views opt in per list — e.g. FreeAgents passes it
   * for the recommendation hero rows only.
   */
  renderMedia?: (p: P) => ReactNode;
  renderMetadata?: (p: P) => ReactNode;
  getReasonTags?: (p: P) => ReactNode[];
  getRole?: (p: P) => ReactNode;
  renderMood?: (p: P) => ReactNode;
  /**
   * Renders action buttons for each player.
   * When `swipeRevealActions` is true, return null to suppress the per-card
   * action area for a given player (e.g. when they have no valid actions).
   */
  renderActions?: (p: P) => ReactNode;
  /**
   * Y-06-c: put renderActions behind an explicit per-card action control instead
   * of the always-visible card footer. Roster opts in; FreeAgents does not.
   * Defaults to false for backwards compatibility.
   */
  swipeRevealActions?: boolean;
};

/** Internal: wraps a card with optional per-card action buttons. */
const SwipeCard = ({
  hasSwipePanel,
  actionsNode,
  footerNode,
  onRevealedChange,
  playerPid,
  children,
}: {
  hasSwipePanel: boolean;
  actionsNode: ReactNode;
  footerNode: ReactNode;
  onRevealedChange: (revealed: boolean) => void;
  playerPid: number;
  children: ReactNode;
}) => {
  const [revealed, setRevealed] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    startX.current = e.clientX;
    startY.current = e.clientY;
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;
    // Only trigger on clearly horizontal swipes (not vertical scroll).
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 44) {
      const nextRevealed = dx < 0;
      setRevealed(nextRevealed);
      onRevealedChange(nextRevealed);
    }
  };

  return (
    <div
      className="card mb-2"
      data-player-card-pid={playerPid}
      data-player-card-actions-state={
        hasSwipePanel ? (revealed ? "open" : "closed") : "unavailable"
      }
    >
      <div
        className="card-body py-2 px-2"
        style={{
          touchAction: "pan-y",
        }}
        onPointerDown={hasSwipePanel ? onPointerDown : undefined}
        onPointerUp={hasSwipePanel ? onPointerUp : undefined}
      >
        {children}
        {footerNode}
        {hasSwipePanel ? (
          <>
            <button
              aria-controls={`player-card-actions-${playerPid}`}
              aria-expanded={revealed}
              className="btn btn-outline-secondary btn-sm w-100 mt-2"
              data-player-card-actions-toggle={playerPid}
              onClick={() => {
                const nextRevealed = !revealed;
                setRevealed(nextRevealed);
                onRevealedChange(nextRevealed);
              }}
              type="button"
            >
              {i18n.t("common.actions", {
                ns: "ui",
                defaultValue: "Actions",
              })}
            </button>
            {revealed ? (
              <div
                className="border-top mt-2 pt-2"
                data-player-card-actions-panel={playerPid}
                id={`player-card-actions-${playerPid}`}
              >
                {actionsNode}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
};

export const PlayerCardList = <P extends BaseCardPlayer>({
  players,
  season,
  shouldShowRatings,
  getJerseyNumber,
  getOvrChange,
  getPotChange,
  getAwards,
  getContract,
  getPlayerHref,
  onPlayerNameClick,
  renderMedia,
  renderMetadata,
  getReasonTags,
  getRole,
  renderMood,
  renderActions,
  swipeRevealActions = false,
}: PlayerCardListProps<P>) => {
  const [expanded, setExpanded] = useState(false);
  const [revealedPids, setRevealedPids] = useState<Set<number>>(() => new Set());
  const listRef = useRef<HTMLDivElement>(null);
  const playerKeySignature = players.map((p) => String(p.pid)).join("\u0001");

  useEffect(() => {
    setExpanded(false);
    setRevealedPids(new Set());
  }, [playerKeySignature]);

  const { canCollapse, visibleCount } = useMobileListVisibleCount({
    expanded,
    itemCount: players.length,
    itemSignature: playerKeySignature,
    listRef,
    measurementFrozen: revealedPids.size > 0,
  });

  if (players.length === 0) {
    return null;
  }

  const playersToRender = canCollapse && !expanded ? players.slice(0, visibleCount) : players;

  return (
    <div
      className="mb-3"
      data-player-card-list=""
      data-player-card-list-expanded={expanded ? "true" : "false"}
      data-player-card-list-total={players.length}
      data-player-card-list-visible={playersToRender.length}
    >
      <div className="player-card-list-mobile" ref={listRef}>
        {playersToRender.map((p) => {
          const showRatings = shouldShowRatings(p);
          const contract = getContract?.(p);
          const moodNode = renderMood?.(p) ?? null;
          const playerHref = getPlayerHref?.(p);
          const actionsNode = renderActions?.(p) ?? null;
          const mediaNode = renderMedia?.(p) ?? null;
          const metadataNode = renderMetadata?.(p) ?? null;
          const reasonTags = getReasonTags?.(p) ?? [];
          const role = getRole?.(p);

          // Swipe panel only when opt-in AND the view returns actual content.
          const hasSwipePanel = swipeRevealActions && actionsNode !== null;

          // Footer row: always shows mood; shows inline actions when NOT swipe mode.
          const hasMood = moodNode !== null;
          const hasInlineActions = !swipeRevealActions && actionsNode !== null;
          const footerNode =
            hasMood || hasInlineActions ? (
              <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                {hasMood ? <div className="flex-shrink-0">{moodNode}</div> : null}
                {hasInlineActions ? (
                  <div className="ms-auto d-flex gap-1">{actionsNode}</div>
                ) : null}
              </div>
            ) : null;

          return (
            <SwipeCard
              key={p.pid}
              hasSwipePanel={hasSwipePanel}
              actionsNode={actionsNode}
              footerNode={footerNode}
              onRevealedChange={(nextRevealed) => {
                setRevealedPids((current) => {
                  const next = new Set(current);
                  if (nextRevealed) {
                    next.add(p.pid);
                  } else {
                    next.delete(p.pid);
                  }
                  return next;
                });
              }}
              playerPid={p.pid}
            >
              <div className="d-flex align-items-start gap-2">
                {mediaNode ? <div className="flex-shrink-0">{mediaNode}</div> : null}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="fw-semibold">
                    <PlayerNameLabels
                      pid={p.pid}
                      injury={p.injury}
                      jerseyNumber={getJerseyNumber(p)}
                      season={season}
                      skills={p.ratings.skills}
                      playerHref={playerHref}
                      playerCardLinkPid={p.pid}
                      onNameClick={
                        onPlayerNameClick ? (event) => onPlayerNameClick(event, p) : undefined
                      }
                      defaultWatch={p.watch}
                      firstName={p.firstName}
                      firstNameShort={p.firstNameShort}
                      lastName={p.lastName}
                      awards={getAwards?.(p)}
                      neverShowCountry
                      nameOnly
                    />
                  </div>
                  <div className="small text-body-secondary mt-1">
                    <span className="me-2">
                      <PositionText pos={p.ratings.pos} display="full" />
                    </span>
                    <span className="me-2">
                      {i18n.t("playerCard.age", {
                        ns: "ui",
                        defaultValue: "Age {{age}}",
                        age: p.age,
                      })}
                    </span>
                    {contract ? (
                      <span className="me-2">
                        {helpers.formatCurrency(contract.amount, "M")} · {contract.exp}
                      </span>
                    ) : null}
                    {role ? <span className="me-2">{role}</span> : null}
                  </div>
                  {reasonTags.length > 0 ? (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {reasonTags.map((tag, i) => (
                        <span className="badge rounded-pill text-bg-light border text-body" key={i}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {metadataNode ? <div className="mt-2">{metadataNode}</div> : null}
                </div>
                {showRatings ? (
                  <div className="text-end" style={{ minWidth: 56 }}>
                    <div className="fw-bold">
                      <RatingWithChange change={getOvrChange?.(p) ?? 0}>
                        {p.ratings.ovr}
                      </RatingWithChange>
                    </div>
                    <div className="small text-body-secondary">
                      /
                      <RatingWithChange change={getPotChange?.(p) ?? 0}>
                        {p.ratings.pot}
                      </RatingWithChange>
                    </div>
                  </div>
                ) : null}
              </div>
            </SwipeCard>
          );
        })}
      </div>
      {canCollapse ? (
        <div data-player-card-list-toggle="">
          <MobileListToggle
            expanded={expanded}
            onClick={() => {
              setExpanded((prev) => !prev);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default PlayerCardList;
